import { Fragment } from 'react';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useTheme } from '../context/ThemeContext';
import { usePlayer } from '../context/PlayerContext';
// Règle de réaction au choix : module PUR, éprouvé par `e2e/chat-feedback.test.mjs` (CCT-257).
import { buildFeedback } from '../utils/chatFeedback';
import { exploreTopic, isExploration, remainingTopics } from '../utils/dialogueExploration';
import { getThemeColors } from '../utils/themeStyles';
import {
  ChatMessage,
  DialogueChoice,
  DialogueNode,
  MissionChatData,
  getMissionChatScript,
  getTreeStepCount,
  isTreeMission,
} from '../data/chatTypes';
import { getScenarioChat } from '../data/scenarios/storyMapper';
import { getMissionPlayer, getScenarioForMission, loadRuntimeScenario } from '../data/scenarios';
import { useMissionScenario } from '../hooks/useMissionScenario';
import { apiFetch } from '../api/client';
import { TYPE_COLOR, glow, glowText } from '../utils/cyb';
import ElementHeader from '../components/ElementHeader';
import HeaderNav from '../components/HeaderNav';
import { ThreadRow, OptionsPanel } from '../components/ChatThread';
import { buildNavSequence, useItemNav, type SeqItem } from '../utils/itemNav';
import { useElementVisited } from '../utils/elementProgress';
import { useAuth } from '../context/AuthContext';
import { useStoryProgress } from '../context/StoryProgressContext';
import { envoyerResultatChat } from '../api/storyAnswers';
import { numParam } from '../utils/routeParams';
import { useT } from '../i18n/useT';
import {
  useChatState,
  ChatItem,
  ChoiceTurnData,
  DisplayMsg,
  AnsweredQuestion,
  buildChatStateKey,
  choicesFromHistory,
} from '../context/ChatStateContext';

// Header de navigation TOUJOURS visible au scroll (CCT-193 #7) : épinglé sur web (le document
// mobile peut scroller). No-op natif / si le ScrollView scrolle déjà en interne.
const WEB_STICKY: any = Platform.OS === 'web' ? { position: 'sticky', top: 0, zIndex: 10 } : null;

// CCT-253 — la table d'avatars joueurs BUNDLÉS a disparu : le joueur parle en son nom.
const PLAYER_AVATAR_FALLBACK = undefined;

function getTimestamp(): string {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function checkAnswer(
  answer: string | string[],
  correctAnswer?: string | string[],
  type?: string,
): boolean {
  if (type === 'free-text') return true;
  if (!correctAnswer) return true;
  if (Array.isArray(correctAnswer)) {
    if (!Array.isArray(answer)) return false;
    return (
      JSON.stringify([...correctAnswer].sort()) ===
      JSON.stringify([...(answer as string[])].sort())
    );
  }
  return answer === correctAnswer;
}

/** Labels deja essayes pour un noeud / une question. */
function getAttemptedLabelsForQuestion(
  questionId: string,
  chatItems: ChatItem[],
  attemptedByNode: Record<string, string[]>,
): string[] {
  const labels = new Set<string>(attemptedByNode[questionId] ?? []);
  for (const item of chatItems) {
    if (item.kind === 'choice-turn' && item.data.nodeId === questionId) {
      item.data.attemptedLabels.forEach(a => labels.add(a));
      if (item.data.selectedLabel) labels.add(item.data.selectedLabel);
      continue;
    }
    if (item.kind !== 'answered' || item.data.question.id !== questionId) continue;
    item.data.attemptedAnswers?.forEach(a => labels.add(a));
    const ans = item.data.selectedAnswer;
    if (typeof ans === 'string') labels.add(ans);
    else if (Array.isArray(ans)) ans.forEach(a => labels.add(a));
  }
  return [...labels];
}

function findLastUnresolvedChoiceTurnIndex(items: ChatItem[], nodeId?: string): number {
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i];
    if (item.kind !== 'choice-turn' || item.data.resolved) continue;
    if (nodeId !== undefined && item.data.nodeId !== nodeId) continue;
    return i;
  }
  return -1;
}

function createChoiceTurn(node: DialogueNode, patch?: Partial<ChoiceTurnData>): ChatItem {
  return {
    kind: 'choice-turn',
    data: {
      id: `ct-${node.id}-${Date.now()}`,
      nodeId: node.id,
      options: node.choices.map(c => ({ id: c.id, label: c.label })),
      attemptedLabels: [],
      pendingLabel: null,
      selectedLabel: null,
      resolved: false,
      ...patch,
    },
  };
}

/** Retire les tours de choix encore actifs pour un noeud (evite les doublons figes). */
function withoutActiveChoiceTurns(items: ChatItem[], nodeId: string): ChatItem[] {
  return items.filter(
    i => !(i.kind === 'choice-turn' && i.data.nodeId === nodeId && !i.data.resolved),
  );
}

/** Place le panneau de choix active en bas du fil (apres les repliques). */
function normalizeChoiceTurnOrder(items: ChatItem[]): ChatItem[] {
  const idx = findLastUnresolvedChoiceTurnIndex(items);
  if (idx < 0 || idx === items.length - 1) return items;
  const turn = items[idx];
  const rest = items.filter((_, i) => i !== idx);
  return [...rest, turn];
}

/** Nettoie un etat sauvegarde (pending fige, panneau au milieu du fil). */
function sanitizeTreeChatItems(items: ChatItem[]): ChatItem[] {
  const cleared = items.map(item =>
    item.kind === 'choice-turn'
      ? { ...item, data: { ...item.data, pendingLabel: null } }
      : item,
  );
  return normalizeChoiceTurnOrder(cleared);
}

function resolveNodeForChoiceTurn(
  tree: { nodes: Record<string, DialogueNode> },
  data: ChoiceTurnData,
): DialogueNode | undefined {
  const byId = tree.nodes[data.nodeId];
  if (byId) return byId;
  // Backward compatibility for stale saved state: recover node from option labels.
  const labels = new Set(data.options.map(o => o.label));
  return Object.values(tree.nodes).find(n => n.choices.some(c => labels.has(c.label)));
}

function TypingDots({
  tutorAvatar,
  tutorColor,
  tutorImage,
  colors,
}: {
  tutorAvatar: string;
  tutorColor: string;
  tutorImage?: any;
  colors: ReturnType<typeof getThemeColors>;
}) {
  const [count, setCount] = useState(1);
  useEffect(() => {
    const id = setInterval(() => setCount(c => (c >= 3 ? 1 : c + 1)), 400);
    return () => clearInterval(id);
  }, []);
  return (
    <View style={td.row}>
      {tutorImage ? (
        <View style={td.avatarWrap}>
          <Image source={tutorImage} style={td.avatarImg} resizeMode="cover" />
        </View>
      ) : (
        <View style={[td.avatar, { backgroundColor: tutorColor }]}>
          <Text style={[td.avatarTxt, { color: '#FFFFFF' }]}>{tutorAvatar}</Text>
        </View>
      )}
      <View style={[td.bubble, { backgroundColor: colors.surfaceSecondary }]}>
        <Text style={[td.dots, { color: colors.textSecondary }]}>
          {'●  '.repeat(count).trimEnd()}
        </Text>
      </View>
    </View>
  );
}

const td = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'flex-end', gap: 8, marginBottom: 8 },
  avatarWrap: {
    width: 32,
    height: 32,
    borderRadius: 16,
    overflow: 'hidden',
    flexShrink: 0,
  },
  avatarImg: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  avatarTxt: { fontSize: 10, fontWeight: 'bold' },
  bubble: {
    borderRadius: 16,
    borderBottomLeftRadius: 4,
    paddingHorizontal: 14,
    paddingVertical: 10,
    minWidth: 72,
  },
  dots: { fontSize: 18, letterSpacing: 4 },
});

export default function MissionChatScreen({ navigation: navProp, route: routeProp, onProgressConfirmed }: { navigation?: any; route?: any; onProgressConfirmed?: () => Promise<void> } = {}) {
  const t = useT();
  const navigation = navProp ?? useNavigation<any>();
  const route = routeProp ?? useRoute<any>();
  const { isDarkTheme } = useTheme();
  const { playerName, playerAvatar } = usePlayer();
  const { getAccessToken } = useAuth();
  const colors = getThemeColors(isDarkTheme);
  const { width } = useWindowDimensions();
  const isWebDesktop = Platform.OS === 'web' && width >= 768;
  useMissionScenario(route.params?.missionId);

  const [freshSequence, setFreshSequence] = useState<SeqItem[] | null>(null);
  const [refreshingNext, setRefreshingNext] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const navRoute = freshSequence ? { ...route, params: { ...route.params,
    itemSeq: freshSequence, itemIndex: freshSequence.findIndex(i => String(i.id) === String(route.params?.itemId)),
  } } : route;
  const itemNav = useItemNav(navRoute, navigation, route.params?.itemId,
    buildNavSequence(getScenarioForMission(Number(route.params?.missionId))?.chapters ?? []));
  const scrollRef = useRef<ScrollView>(null);
  const chatStateStore = useChatState();
  const { loadProgress } = useStoryProgress();

  // CCT-252 — par URL, ce param arrive en CHAÎNE. `mockChatData.find(d => d.missionId === missionId)`
  // comparait alors `3 === "3"` : mission jamais trouvée, écran « Mission introuvable » sur une
  // mission qui existe. Ne se voyait que par URL collée / F5, jamais en cliquant.
  const missionId: number | undefined = numParam(route.params?.missionId);
  const itemId: string | number | undefined = route.params?.itemId;

  // Both the mobile stack and the desktop panel must receive the server-unlocked content
  // before offering the next item. Updating only completion badges leaves gated shells cached.
  const refreshNext = useCallback(async () => {
    if (missionId == null) return;
    setRefreshingNext(true);
    setRefreshFailed(false);
    try {
      const token = getAccessToken();
      if (!token) throw new Error('Session unavailable');
      await loadRuntimeScenario(missionId,
        () => apiFetch(`/me/stories/${missionId}/scenario`, { token, auth: true }), true);
      await loadProgress(missionId);
      setFreshSequence(buildNavSequence(getScenarioForMission(missionId)?.chapters ?? []));
      await onProgressConfirmed?.();
    } catch {
      setRefreshFailed(true);
    } finally {
      setRefreshingNext(false);
    }
  }, [missionId, getAccessToken, loadProgress, onProgressConfirmed]);

  useEffect(() => {
    setFreshSequence(null);
    setRefreshFailed(false);
  }, [missionId, itemId]);

  // From chapter sequence → JSON chat; from global chat icon → legacy mock.
  const scenarioChat = missionId !== undefined ? getScenarioChat(missionId, itemId != null ? String(itemId) : undefined) : undefined;
  // CCT-471 — SCORING. The threshold the author set on the mission, resolved onto this item by
  // the server; `undefined` = no bar.
  const seuilChat = scenarioChat?.chatPassPercent;
  // ⚠️ ABSENT MEANS SHOW. An older payload carries no flag, and reading its absence as "hide"
  // would silently mute the verdict on every mission already being played.
  const montrerVerdict = scenarioChat?.chatShowVerdict !== false;
  // Progress DRIVEN BY THE MODEL (see utils/elementProgress): the DISPLAYED element is the one
  // recorded, whatever path was taken to open it.
  //
  // ⚠️ EXCEPT WHEN A THRESHOLD IS SET, and that is the whole bar. `completeItem` marks an element
  // OPENED: letting the opening validate it would make the threshold avoidable by walking in and
  // straight back out. A scored chat is validated only by the server, on the recomputed score.
  useElementVisited(route?.params?.missionId, route?.params?.itemId, seuilChat == null);
  const missionData: MissionChatData | undefined = scenarioChat
    ? {
        missionId: scenarioChat.missionId,
        missionTitle: scenarioChat.missionTitle,
        tutorName: scenarioChat.tutorName,
        tutorAvatar: scenarioChat.tutorAvatar,
        tutorColor: scenarioChat.tutorColor,
        tutorImage: scenarioChat.tutorImage,
        tutorRole: scenarioChat.tutorRole,
        unreadCount: 0,
        lastActivity: '',
        messages: scenarioChat.messages as ChatMessage[],
      }
    : undefined;   // plus de repli sur les conversations bundlees (CCT-253)
  const chatStateKey = missionId !== undefined ? buildChatStateKey(missionId, itemId) : null;
  // CCT-152 — dans la conversation, le joueur parle EN SON NOM : avatar et nom de son profil.
  // Le personnage incarné (`getMissionPlayer`) reste une information de CASTING, affichée
  // dans la fiche des personnages, pas une identité de substitution.
  const playerAvatarImg = playerAvatar
    ?? PLAYER_AVATAR_FALLBACK;
  const playerLabel = playerName ?? null;

  const [chatItems, setChatItems] = useState<ChatItem[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [activeQ, setActiveQ] = useState<ChatMessage | null>(null);
  const [selectedOpts, setSelectedOpts] = useState<string[]>([]);
  const [freeInput, setFreeInput] = useState('');
  const [answeredCount, setAnsweredCount] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [attemptedByNode, setAttemptedByNode] = useState<Record<string, string[]>>({});
  const [pendingChoiceLabel, setPendingChoiceLabel] = useState<string | null>(null);
  // CCT-471 — what the SERVER answered: the score it recomputed, and whether the bar opened.
  // `null` while the result has not come back, which is also what a network failure leaves.
  const [resultatChat, setResultatChat] = useState<
    { score: number | null; total: number; passed: boolean; threshold: number | null; repondues: number } | null
  >(null);
  const [envoiEchoue, setEnvoiEchoue] = useState(false);

  // CCT-471 — the labels chosen, question by question, for the server to grade. A ref and not
  // state: nothing on screen depends on it, and a re-render per answer would restart the typing
  // timers.
  const choixRef = useRef<Record<string, string>>({});
  const exploredRef = useRef<Record<string, string[]>>({});
  const resultatEnvoyeRef = useRef(false);
  const scriptRef = useRef<ChatMessage[]>([]);
  const posRef = useRef(0);
  const currentNodeIdRef = useRef('');
  const isRunRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const choiceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isTree = missionData ? isTreeMission(missionData) : false;
  const conversationTree = missionData?.conversationTree;

  const totalSteps = missionData
    ? isTree && conversationTree
      ? getTreeStepCount(conversationTree)
      // Les choix de dialogue ne comptent pas : le total annonce ce que l'apprenant aura à
      // DÉMONTRER, pas le nombre de fois où il aura pris la parole.
      : getMissionChatScript(missionData)
          .filter(m => m.type && m.type !== 'text' && m.type !== 'dialogue-choice').length
    : 0;

  // Accent de CET écran : couleur du type d'élément (chat = orange) — c'est elle qui pilote
  // les surbrillances, en VOILE (fond teinté + bord lumineux), jamais en aplat plein.
  const ACCENT = TYPE_COLOR.chat;
  const tutorColor = missionData?.tutorColor ?? ACCENT;

  const addItem = useCallback((item: ChatItem) => {
    setChatItems(prev => [...prev, item]);
  }, []);

  const patchChatItems = useCallback((patcher: (items: ChatItem[]) => ChatItem[]) => {
    setChatItems(prev => patcher(prev));
  }, []);

  const processNext = useCallback(() => {
    if (isRunRef.current) return;

    const pos = posRef.current;
    const script = scriptRef.current;

    if (pos >= script.length) {
      setIsComplete(true);
      return;
    }

    const item = script[pos];
    isRunRef.current = true;
    setIsTyping(true);

    timerRef.current = setTimeout(() => {
      isRunRef.current = false;
      setIsTyping(false);

      addItem({
        kind: 'msg',
        data: {
          id: `t-${pos}`,
          sender: 'tutor' as const,
          text: item.text,
          timestamp: getTimestamp(),
          characterImage: item.characterImage,
          characterName: item.characterName,
          // CCT-477 — the diagram the author attached to this turn.
          diagramUrl: item.diagramUrl,
          diagramZoom: item.diagramZoom,
        },
      });

      posRef.current = pos + 1;

      if (!item.type || item.type === 'text') {
        processNext();
      } else {
        setActiveQ(item);
      }
    }, 900);
  }, [addItem]);

  const showNode = useCallback(
    (nodeId: string, opts?: { replayPrompt?: boolean }) => {
      if (!conversationTree) return;
      const node = conversationTree.nodes[nodeId];
      if (!node) {
        console.warn('[MissionChat] showNode missing node', nodeId);
        setIsComplete(true);
        return;
      }

      currentNodeIdRef.current = nodeId;
      console.log('[MissionChat] showNode', { nodeId, isEnd: Boolean(node.isEnd), replayPrompt: opts?.replayPrompt });

      if (node.isEnd) {
        isRunRef.current = true;
        setIsTyping(true);
        timerRef.current = setTimeout(() => {
          isRunRef.current = false;
          setIsTyping(false);
          addItem({
            kind: 'msg',
            data: {
              id: `t-end-${nodeId}`,
              sender: 'tutor',
              text: node.tutorText,
              timestamp: getTimestamp(),
            },
          });
          setIsComplete(true);
        }, 900);
        return;
      }

      const shouldShowPrompt = opts?.replayPrompt !== false;
      if (!shouldShowPrompt) return;

      isRunRef.current = true;
      setIsTyping(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        isRunRef.current = false;
        setIsTyping(false);
        setChatItems(prev => [
          ...prev,
          {
            kind: 'msg',
            data: {
              id: `t-node-${nodeId}-${Date.now()}`,
              sender: 'tutor',
              text: node.tutorText,
              timestamp: getTimestamp(),
            },
          },
          createChoiceTurn(node),
        ]);
      }, 900);
    },
    [addItem, conversationTree],
  );

  useEffect(() => {
    if (!missionData || missionId === undefined || !chatStateKey) return;

    const saved = chatStateStore.getState(chatStateKey);

    if (isTree && conversationTree) {
      if (saved && saved.chatItems.length > 0) {
        setChatItems(sanitizeTreeChatItems(saved.chatItems));
        setAnsweredCount(saved.answeredCount);
        setIsComplete(saved.isComplete);
        setIsTyping(false);
        setPendingChoiceLabel(null);
        if (saved.attemptedChoicesByNode) {
          setAttemptedByNode(saved.attemptedChoicesByNode);
        }
        const nodeId = saved.currentNodeId ?? conversationTree.startNodeId;
        currentNodeIdRef.current = nodeId;
        if (!saved.isComplete) {
          const node = conversationTree.nodes[nodeId];
          if (node && !node.isEnd && node.choices.length > 0) {
            const hasOpenTurn = saved.chatItems.some(
              i =>
                i.kind === 'choice-turn' &&
                i.data.nodeId === nodeId &&
                !i.data.resolved,
            );
            if (!hasOpenTurn) {
              setChatItems(prev => [...prev, createChoiceTurn(node)]);
            }
          }
        }
      } else {
        currentNodeIdRef.current = conversationTree.startNodeId;
        showNode(conversationTree.startNodeId);
      }
    } else {
      scriptRef.current = getMissionChatScript(missionData);

      if (saved && saved.chatItems.length > 0) {
        setChatItems(saved.chatItems);
        // Restore the chosen labels too: reopening must not submit an empty attempt.
        choixRef.current = choicesFromHistory(saved.chatItems);
        posRef.current = saved.pos;
        setAnsweredCount(saved.answeredCount);
        setIsComplete(saved.isComplete);
        if (!saved.isComplete) {
          const pending = scriptRef.current[Math.max(0, saved.pos - 1)];
          const used = pending ? getAttemptedLabelsForQuestion(pending.id, saved.chatItems, {}) : [];
          if (pending && isExploration(pending) && remainingTopics(pending, used).length) {
            exploredRef.current[pending.id] = used;
            setActiveQ(pending);
          } else {
            processNext();
          }
        }
      } else {
        posRef.current = 0;
        processNext();
      }
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (choiceTimerRef.current) clearTimeout(choiceTimerRef.current);
      isRunRef.current = false;
    };
  // A deep link loads the script after mounting; start when it actually arrives.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatStateKey, !!missionData]);

  useEffect(() => {
    if (missionId === undefined || !chatStateKey) return;
    chatStateStore.setState(chatStateKey, {
      chatItems,
      pos: posRef.current,
      answeredCount,
      isComplete,
      currentNodeId: isTree ? currentNodeIdRef.current : undefined,
      attemptedChoicesByNode: attemptedByNode,
    });
  }, [chatItems, answeredCount, isComplete, missionId, chatStateKey, chatStateStore, isTree, attemptedByNode]);

  // CCT-471 — the conversation is over: the server grades it and, if it passes, closes the
  // element. Only for a chat carrying a threshold, since that is the only case where the opening
  // no longer validated it.
  //
  // ⚠️ ONCE PER SCREEN, via a ref. This effect depends on `isComplete`, which also drives the
  // save above: without the guard, every state change after the end would resend the result and
  // the journal would show attempt after attempt the player never made.
  useEffect(() => {
    if (!isComplete || missionId === undefined || itemId == null) return;
    if (seuilChat == null || resultatEnvoyeRef.current) return;
    resultatEnvoyeRef.current = true;
    void (async () => {
      const token = getAccessToken();
      const choix = choixRef.current;
      const repondues = Object.keys(choix).length;
      // What WE believe, sent alongside so a disagreement with the server is readable rather
      // than arbitrated in silence. It is never the reference: we hold the right answers.
      const annonce = totalSteps > 0 ? Math.round((answeredCount * 100) / totalSteps) : null;
      const r = await envoyerResultatChat(token, missionId, String(itemId), choix, annonce);
      // ⚠️ A FAILURE LEAVES THE ELEMENT OPEN, deliberately. The bar is the server's answer:
      // pretending it passed would hand out the unlock the threshold exists to withhold. The
      // ref is released so leaving and coming back retries.
      if (!r) { resultatEnvoyeRef.current = false; setEnvoiEchoue(true); }
      else {
        setEnvoiEchoue(false);
        setResultatChat({ ...r, repondues });
        if (r.passed) {
          await refreshNext();
        }
      }
    })();
  }, [isComplete, missionId, itemId, seuilChat, getAccessToken, answeredCount, totalSteps]);

  // Auto-défilement vers le DERNIER message — mais jamais au premier rendu : l'effet se
  // déclenchait aussi au chargement de l'historique, si bien qu'un chat déjà avancé s'ouvrait
  // en plein milieu de la conversation au lieu de son début.
  const didAutoScroll = useRef(false);
  useEffect(() => {
    if (!didAutoScroll.current) { didAutoScroll.current = true; return; }
    const t = setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 80);
    return () => clearTimeout(t);
  }, [chatItems, isTyping, activeQ, pendingChoiceLabel]);

  const handleTreeChoice = useCallback(
    (choice: DialogueChoice, node: DialogueNode) => {
      if (!conversationTree) return;
      if (isTyping) {
        const isActuallyRunning = Boolean(timerRef.current || choiceTimerRef.current);
        if (isActuallyRunning) {
          console.log('[MissionChat] Ignored click while typing', { nodeId: node.id, choiceId: choice.id });
          return;
        }
        console.warn('[MissionChat] Recovered from stale typing lock');
        setIsTyping(false);
      }

      console.log('[MissionChat] handleTreeChoice', {
        nodeId: node.id,
        choiceId: choice.id,
        advances: choice.advances,
        nextNodeId: choice.nextNodeId,
      });

      const nodeId = node.id;
      const attemptedSnapshot = [
        ...new Set([...(attemptedByNode[nodeId] ?? []), choice.label]),
      ];
      setAttemptedByNode(prev => ({ ...prev, [nodeId]: attemptedSnapshot }));

      const feedbackMsg: ChatItem = {
        kind: 'msg',
        data: {
          id: `f-${choice.id}-${Date.now()}`,
          sender: 'tutor',
          text: choice.tutorResponse,
          timestamp: getTimestamp(),
          isCorrect: node.isAssessment ? choice.advances : undefined,
        },
      };

      patchChatItems(prev => {
        const next = withoutActiveChoiceTurns(prev, nodeId);
        next.push(
          createChoiceTurn(node, {
            attemptedLabels: attemptedSnapshot,
            selectedLabel: choice.label,
            resolved: true,
          }),
        );
        next.push(feedbackMsg);
        if (!choice.advances) {
          const nextNode = conversationTree.nodes[choice.nextNodeId] ?? node;
          next.push(
            createChoiceTurn(nextNode, {
              attemptedLabels: nextNode.id === nodeId ? attemptedSnapshot : [],
            }),
          );
        }
        return next;
      });

      currentNodeIdRef.current = choice.nextNodeId;
      setPendingChoiceLabel(null);

      if (!choice.advances) {
        return;
      }

      isRunRef.current = true;
      setIsTyping(true);
      if (choiceTimerRef.current) clearTimeout(choiceTimerRef.current);
      choiceTimerRef.current = setTimeout(() => {
        choiceTimerRef.current = null;
        isRunRef.current = false;
        setIsTyping(false);
        setPendingChoiceLabel(null);
        setAnsweredCount(c => c + 1);
        setAttemptedByNode(prev => {
          const next = { ...prev };
          delete next[nodeId];
          return next;
        });
        console.log('[MissionChat] advancing to node', choice.nextNodeId);
        showNode(choice.nextNodeId);
      }, 700);
    },
    [patchChatItems, showNode, conversationTree, attemptedByNode, isTyping],
  );

  const handleAnswer = useCallback(
    (answer: string | string[]) => {
      if (!activeQ) return;
      const q = activeQ;

      if (isExploration(q)) {
        if (typeof answer !== 'string') return;
        const used = exploredRef.current[q.id] ?? getAttemptedLabelsForQuestion(q.id, chatItems, {});
        const result = exploreTopic(q, used, answer);
        if (!result) return;
        exploredRef.current[q.id] = result.used;
        // Store the question choice and its reply together, including when leaving immediately.
        patchChatItems(prev => [...prev,
          { kind: 'answered', data: { id: `aq-${q.id}-${result.used.length}`, question: q,
            selectedAnswer: answer, attemptedAnswers: result.used, timestamp: getTimestamp() } },
          { kind: 'msg', data: { id: `f-${q.id}-${result.used.length}`, sender: 'tutor',
            text: result.reply, timestamp: getTimestamp(),
            characterImage: q.characterImage, characterName: q.characterName } },
        ]);
        if (!result.remaining.length) {
          setActiveQ(null);
          processNext();
        }
        return;
      }

      if (q.type !== 'free-text') {
        const attemptedAnswers = Array.isArray(answer)
          ? answer
          : [answer as string];
        addItem({
          kind: 'answered',
          data: {
            id: `aq-${Date.now()}`,
            question: q,
            selectedAnswer: answer,
            attemptedAnswers,
            timestamp: getTimestamp(),
          },
        });
      }

      if (q.type === 'free-text') {
        const ansText = Array.isArray(answer) ? answer.join(', ') : answer;
        addItem({
          kind: 'msg',
          data: {
            id: `u-${Date.now()}`,
            sender: 'user' as const,
            text: ansText,
            timestamp: getTimestamp(),
          },
        });
      }

      setActiveQ(null);
      setSelectedOpts([]);
      setFreeInput('');
      // ⚠️ UN CHOIX DE DIALOGUE N'EST PAS UNE QUESTION RÉPONDUE. Le joueur choisit ce qu'il
      // DEMANDE ; rien n'est évalué. Le compter gonflait « 2/2 questions répondues » d'un tour
      // où l'apprenant n'avait rien démontré.
      const estDialogue = q.type === 'dialogue-choice';
      if (!estDialogue) setAnsweredCount(c => c + 1);

      // ⚠️ ET IL N'A NI JUSTE NI FAUX. `checkAnswer` renvoie « juste » dès qu'aucune bonne
      // réponse n'est déclarée : poser une question allumait une bulle verte « correct ».
      // `undefined` laisse la bulle neutre — c'est déjà ce que fait l'arbre de scénario pour un
      // nœud qui n'est pas une évaluation.
      const isCorrect = estDialogue
        ? undefined
        : checkAnswer(answer, q.correctAnswer, q.type);

      // Preserve dialogue arbitrations as well as assessed answers. The server ignores
      // ungraded questions when scoring, but later scenes need the player's actual choice.
      if (typeof answer === 'string') {
        choixRef.current[String(q.id)] = answer;
      }

      // ⚠️ THE VERDICT IS HIDDEN, THE SCORE IS NOT. `montrerVerdict` false masks ok/ko on screen
      // and the "Correct!" wording, while `isCorrect` above keeps grading: an author who turns
      // the verdict off still wants a threshold to mean something.
      const verdictAffiche = montrerVerdict ? isCorrect : undefined;

      setIsTyping(true);
      timerRef.current = setTimeout(() => {
        setIsTyping(false);
        addItem({
          kind: 'msg',
          data: {
            id: `f-${Date.now()}`,
            sender: 'tutor' as const,
            text: buildFeedback(verdictAffiche, q, answer, t),
            timestamp: getTimestamp(),
            isCorrect: verdictAffiche,
            characterImage: q.characterImage,
            characterName: q.characterName,
          },
        });
        processNext();
      }, 900);
    },
    [activeQ, addItem, processNext, montrerVerdict, chatItems, patchChatItems],
  );

  const toggleOpt = (opt: string) =>
    setSelectedOpts(prev => (prev.includes(opt) ? prev.filter(o => o !== opt) : [...prev, opt]));


  // Rendu délégué au moteur de chat UNIQUE (CCT-99) : ce chat est la RÉFÉRENCE visuelle,
  // il doit donc utiliser le même composant que les autres — sinon c'est lui qui diverge.
  const renderMessage = (msg: DisplayMsg) => (
    <ThreadRow
      key={msg.id}
      m={{
        role: msg.sender === 'user' ? 'student' : 'character',
        text: msg.text,
        speaker: msg.characterName ?? missionData?.tutorName ?? undefined,
        avatarSource: msg.characterImage ?? missionData?.tutorImage,
        // CCT-477 — carried to the single renderer, which draws it inside the bubble.
        diagramUrl: msg.diagramUrl,
        diagramZoom: msg.diagramZoom,
      }}
      characterName={missionData?.tutorName}
      playerAvatar={playerAvatarImg}
      playerName={playerLabel}
      tone={msg.isCorrect === true ? 'ok' : msg.isCorrect === false ? 'ko' : undefined}
    />
  );
;

  /** Le panneau PARTAGÉ porte déjà sa ligne et l'avatar du joueur : l'envelopper à nouveau
   *  produisait une seconde ligne (et un second avatar), d'où une géométrie différente des
   *  autres chats. On ne garde qu'une clé. */
  const keyed = (key: string, node: React.ReactNode) => (
    <Fragment key={key}>{node}</Fragment>
  );

  /** Wrapper de ligne pour les blocs QUI NE SONT PAS le panneau partagé (validation d'un
   *  choix multiple, saisie libre) : eux n'ont pas de ligne à eux. */
  const wrapUserChoiceArea = (key: string, children: React.ReactNode) => (
    <View key={key} style={[styles.msgRow, styles.msgRowRight]}>
      <View style={[styles.msgContent, styles.msgContentRight, styles.choiceColumn]}>
        {children}
      </View>
      <View style={styles.msgAvatarWrap}>
        <Image
          source={playerAvatarImg ?? PLAYER_AVATAR_FALLBACK}
          style={styles.msgAvatarImage}
          resizeMode="cover"
        />
      </View>
    </View>
  );

  // Panneau de choix délégué au composant PARTAGÉ (CCT-99) : c'est cette variante riche
  // (tentées / verrouillée / en attente) qui fait référence, les autres chats l'appellent
  // sans ces options. Le panneau vivait ici, d'où la divergence visuelle entre les écrans.
  const renderChoicesPanel = (
    questionId: string,
    options: { id: string; label: string }[],
    config: {
      hint: string;
      readOnly?: boolean;
      selectedLabel?: string | null;
      selectedLabels?: string[];
      attemptedOverride?: string[];
      pendingLabel?: string | null;
      onSelect?: (label: string) => void;
    },
  ) => (
    <OptionsPanel
      options={options}
      hint={config.hint}
      readOnly={config.readOnly}
      busy={isTyping}
      selectedLabel={config.selectedLabel}
      selectedLabels={config.selectedLabels}
      attempted={config.attemptedOverride
        ?? getAttemptedLabelsForQuestion(questionId, chatItems, attemptedByNode)}
      pendingLabel={config.pendingLabel}
      playerAvatar={playerAvatarImg}
      playerName={playerLabel}
      onChoose={(opt) => config.onSelect?.(opt.label)}
    />
  );
;

  const renderAnsweredQuestion = (item: AnsweredQuestion) => {
    const { question: q, selectedAnswer } = item;

    // Keep only the spoken choice in the transcript. The active panel still shows
    // every topic, with answered topics disabled, until the exploration is over.
    if (q.type === 'dialogue-choice') {
      if (typeof selectedAnswer !== 'string' || !selectedAnswer) return null;
      return keyed(item.id, renderMessage({
        id: item.id, sender: 'user', text: selectedAnswer, timestamp: item.timestamp,
      }));
    }

    if (q.type === 'single-choice' && q.options) {
      const chosen = typeof selectedAnswer === 'string' ? selectedAnswer : '';
      if (!chosen) return null;
      return keyed(
        item.id,
        renderChoicesPanel(
          q.id,
          q.options.map((label, i) => ({ id: `${item.id}-opt-${i}`, label })),
          {
            hint: t('chat.yourAnswer'),
            readOnly: true,
            selectedLabel: chosen,
            attemptedOverride: item.attemptedAnswers,
          },
        ),
      );
    }

    if (q.type === 'multiple-choice' && q.options) {
      const chosen: string[] = Array.isArray(selectedAnswer) ? selectedAnswer : [];
      if (chosen.length === 0) return null;
      return keyed(
        item.id,
        renderChoicesPanel(
          q.id,
          q.options.map((label, i) => ({ id: `${item.id}-opt-${i}`, label })),
          {
            hint: t('missionChat.yourAnswers'),
            readOnly: true,
            selectedLabels: chosen,
            attemptedOverride: item.attemptedAnswers ?? chosen,
          },
        ),
      );
    }

    return null;
  };

  const renderChoiceTurn = (data: ChoiceTurnData) => {
    const node = conversationTree ? resolveNodeForChoiceTurn(conversationTree, data) : undefined;
    return keyed(
      data.id,
      renderChoicesPanel(data.nodeId, data.options, {
        hint: data.resolved
          ? t('chat.yourAnswer')
          : t('chat.pickAnswerToContinue'),
        readOnly: data.resolved,
        selectedLabel: data.selectedLabel,
        attemptedOverride: data.attemptedLabels,
        pendingLabel: data.resolved ? null : pendingChoiceLabel,
        onSelect:
          !data.resolved && node
            ? label => {
                const choice = node.choices.find(c => c.label === label);
              if (choice) {
                handleTreeChoice(choice, node);
                return;
              }
              console.warn('[MissionChat] No choice matched label', {
                nodeId: node.id,
                label,
              });
              }
            : undefined,
      }),
    );
  };

  const renderActiveQuestion = () => {
    if (isTree) return null;
    if (!activeQ) return null;

    // `dialogue-choice` était déjà rendu UNE FOIS RÉPONDU, mais jamais proposé : le sélecteur
    // actif ne connaissait que `single-choice`. Le type existait donc sans être jouable.
    if ((activeQ.type === 'single-choice' || activeQ.type === 'dialogue-choice')
        && activeQ.options) {
      return keyed(
        `active-q-${activeQ.id}`,
        renderChoicesPanel(
          activeQ.id,
          activeQ.options.map((label, i) => ({ id: `${activeQ.id}-opt-${i}`, label })),
          {
            hint: t(activeQ.type === 'dialogue-choice' ? 'chat.pickQuestion' : 'chat.pickAnswer'),
            onSelect: label => handleAnswer(label),
          },
        ),
      );
    }

    if (activeQ.type === 'multiple-choice' && activeQ.options) {
      return wrapUserChoiceArea(
        `active-q-${activeQ.id}`,
        <View style={styles.choiceColumn}>
          {renderChoicesPanel(
            activeQ.id,
            activeQ.options.map((label, i) => ({ id: `${activeQ.id}-opt-${i}`, label })),
            {
              hint: t('chat.pickOneOrMore'),
              selectedLabels: selectedOpts,
              onSelect: label => toggleOpt(label),
            },
          )}
          <TouchableOpacity
            style={[
              styles.validateBtn,
              styles.optBtnFullWidth,
              { backgroundColor: selectedOpts.length > 0 ? ACCENT : colors.surfaceSecondary },
            ]}
            onPress={() => { if (selectedOpts.length > 0) handleAnswer(selectedOpts); }}
            disabled={selectedOpts.length === 0 || isTyping}
          >
            <Text
              style={[
                styles.validateBtnText,
                { color: selectedOpts.length > 0 ? colors.badgeText : colors.textTertiary },
              ]}
            >
              {selectedOpts.length > 0 ? `Valider (${selectedOpts.length} selectionne${selectedOpts.length > 1 ? 's' : ''})` : 'Valider'}
            </Text>
          </TouchableOpacity>
        </View>,
      );
    }

    if (activeQ.type === 'free-text') {
      return wrapUserChoiceArea(
        `active-q-${activeQ.id}`,
        <View style={styles.inlineOptions}>
          <Text style={[styles.optHint, { color: colors.textSecondary }]}>
            {t('missionChat.freeText')}
          </Text>
          <View style={styles.freeRow}>
            <TextInput
              style={[
                styles.freeInput,
                {
                  backgroundColor: colors.inputBackground,
                  color: colors.inputText,
                  borderColor: colors.inputBorder,
                },
              ]}
              placeholder={t('chat.yourAnswerPlaceholder')}
              placeholderTextColor={colors.inputPlaceholder}
              value={freeInput}
              onChangeText={setFreeInput}
              multiline
              maxLength={500}
            />
            <TouchableOpacity
              style={[
                styles.sendBtn,
                { backgroundColor: freeInput.trim() ? ACCENT : colors.surfaceSecondary },
              ]}
              onPress={() => { if (freeInput.trim()) handleAnswer(freeInput.trim()); }}
              disabled={!freeInput.trim()}
            >
              <Ionicons
                name="send"
                size={20}
                color={freeInput.trim() ? colors.badgeText : colors.textTertiary}
              />
            </TouchableOpacity>
          </View>
        </View>,
      );
    }

    return null;
  };

  if (!missionId || !missionData) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={[styles.header, WEB_STICKY, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
          <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={24} color={colors.iconPrimary} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>{t('missionChat.title')}</Text>
          <View style={{ width: 32 }} />
        </View>
        <View style={styles.emptyState}>
          <Ionicons name="chatbox-outline" size={64} color={colors.textTertiary} />
          <Text style={[styles.emptyTitle, { color: colors.textPrimary }]}>{t('missionChat.notFound')}</Text>
          <Text style={[styles.emptySubtitle, { color: colors.textSecondary }]}>
            {t('chat.loadFailed')}
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[
        styles.container,
        { backgroundColor: isWebDesktop ? colors.surfaceSecondary : colors.background },
      ]}
    >
      <View style={[styles.header, WEB_STICKY, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color={colors.iconPrimary} />
        </TouchableOpacity>
        {missionData.tutorImage ? (
          <View style={styles.headerAvatarWrap}>
            <Image source={missionData.tutorImage} style={styles.headerAvatarImage} resizeMode="cover" />
          </View>
        ) : (
          <View style={[styles.headerAvatar, { backgroundColor: tutorColor }]}>
            <Text style={[styles.headerAvatarText, { color: '#FFFFFF' }]}>
              {missionData.tutorAvatar}
            </Text>
          </View>
        )}
        <View style={styles.headerInfo}>
        <ElementHeader
          missionTitle={(route.params as any)?.missionTitle ?? missionData.missionTitle}
          chapterTitle={(route.params as any)?.chapterTitle ?? ''}
          elementTitle={missionData.tutorName}
        />
        </View>
        {totalSteps > 0 && <View style={[styles.progressBadge, { backgroundColor: colors.accent }]}>
          <Ionicons name="checkmark-circle-outline" size={14} color={ACCENT} />
          <Text style={[styles.progressText, { color: ACCENT }]}>
            {answeredCount}/{totalSteps}
          </Text>
        </View>}
        {/* Navigation TOUJOURS visible : l'ancienne barre n'apparaissait qu'une fois la
            conversation terminée (`isComplete`) — donc jamais tant qu'on discutait. */}
        <HeaderNav onPrev={itemNav.goPrev}
          onNext={refreshingNext || refreshFailed ? undefined : itemNav.goNext}
          accent={ACCENT} isLast={itemNav.isLast} />
      </View>

      <View style={[styles.outer, isWebDesktop && styles.outerDesktop]}>
        <KeyboardAvoidingView
          style={[
            styles.chatBox,
            isWebDesktop && styles.chatBoxDesktop,
            { backgroundColor: colors.background },
          ]}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          keyboardVerticalOffset={90}
        >
          <ScrollView
            ref={scrollRef}
            style={styles.msgList}
            contentContainerStyle={styles.msgListContent}
            showsVerticalScrollIndicator={false}
          >
            {chatItems.map(item => {
              if (item.kind === 'msg') return renderMessage(item.data);
              if (item.kind === 'choice-turn') return renderChoiceTurn(item.data);
              return renderAnsweredQuestion(item.data);
            })}
            {isTyping && (
              <TypingDots
                tutorAvatar={missionData.tutorAvatar}
                tutorColor={tutorColor}
                tutorImage={missionData.tutorImage}
                colors={colors}
              />
            )}
            {renderActiveQuestion()}
            {isComplete && !isTyping && (
              <View
                style={[
                  styles.completionBanner,
                  { backgroundColor: colors.accent, borderColor: ACCENT },
                ]}
              >
                <Ionicons name="trophy-outline" size={22} color={ACCENT} />
                <Text style={[styles.completionText, { color: ACCENT }]}>
                  {totalSteps > 0
                    ? `${t('common.completed')} — ${answeredCount}/${totalSteps} ${t(isTree ? 'missionChat.exchanges' : 'missionChat.questionsAnswered')}`
                    : t('common.completed')}
                </Text>
              </View>
            )}
            {/* CCT-471 — a SCORED chat says where it stands, and names the bar. A score on its
                own leaves the player guessing why the next chapter did or did not open. */}
            {isComplete && !isTyping && seuilChat != null && (totalSteps > 0 || !resultatChat?.passed) && (
              <View
                style={[
                  styles.completionBanner,
                  {
                    backgroundColor: colors.accent,
                    borderColor: envoiEchoue || resultatChat?.passed === false
                      ? colors.warning
                      : ACCENT,
                  },
                ]}
              >
                <Ionicons
                  name={
                    envoiEchoue ? 'cloud-offline-outline'
                      : resultatChat == null ? 'hourglass-outline'
                        : resultatChat.passed ? 'lock-open-outline' : 'lock-closed-outline'
                  }
                  size={22}
                  color={envoiEchoue || resultatChat?.passed === false ? colors.warning : ACCENT}
                />
                <Text
                  style={[
                    styles.completionText,
                    { color: envoiEchoue || resultatChat?.passed === false ? colors.warning : ACCENT },
                  ]}
                >
                  {envoiEchoue
                    ? t('missionChat.scoreFailed')
                    : resultatChat == null
                      ? t('missionChat.scoreSending')
                      : resultatChat.total === 0
                        ? t('missionChat.scoreNothingToGrade')
                        : t(resultatChat.passed ? 'missionChat.scorePassed' : 'missionChat.scoreMissed', {
                          score: String(resultatChat.score ?? 0),
                          threshold: String(resultatChat.threshold ?? seuilChat),
                        })}
                </Text>
              </View>
            )}
            {isComplete && !isTyping && (seuilChat == null || resultatChat?.passed) && (
              <TouchableOpacity accessibilityRole="button"
                disabled={refreshingNext || (seuilChat != null && !freshSequence && !refreshFailed)}
                onPress={refreshFailed ? refreshNext : itemNav.goNext}
                style={[styles.validateBtn, { backgroundColor: ACCENT, opacity: refreshingNext ? 0.6 : 1 }]}>
                <Text style={[styles.validateBtnText, { color: '#08111F' }]}>
                  {refreshingNext ? t('chapter.loading') : refreshFailed ? t('common.retry')
                    : itemNav.isLast ? t('common.back') : t('nav.next')}
                </Text>
              </TouchableOpacity>
            )}
          </ScrollView>

        </KeyboardAvoidingView>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, minHeight: 0 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    gap: 10,
  },
  backBtn: { padding: 4 },
  headerAvatarWrap: { width: 38, height: 38, borderRadius: 19, overflow: 'hidden', flexShrink: 0 },
  headerAvatarImage: { width: 38, height: 38, borderRadius: 19 },
  headerAvatar: { width: 38, height: 38, borderRadius: 19, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  headerAvatarText: { fontSize: 13, fontWeight: 'bold' },
  headerInfo: { flex: 1, minWidth: 0 },
  headerTitle: { fontSize: 15, fontWeight: '700' },
  headerSubtitle: { fontSize: 12, marginTop: 1 },
  progressBadge: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 12 },
  progressText: { fontSize: 13, fontWeight: '700' },
  outer: { flex: 1 },
  outerDesktop: { alignItems: 'center', paddingHorizontal: 24, paddingVertical: 16 },
  chatBox: { flex: 1 },
  chatBoxDesktop: { width: '100%' as any, maxWidth: 800, borderRadius: 16, overflow: 'hidden', shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.1, shadowRadius: 20, elevation: 8 },
  msgList: { flex: 1 },
  msgListContent: { padding: 16, paddingBottom: 24 },
  msgRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 8, marginBottom: 12 },
  msgRowLeft: { justifyContent: 'flex-start' },
  msgRowRight: { justifyContent: 'flex-end' },
  msgAvatarWrap: { width: 32, height: 32, borderRadius: 16, overflow: 'hidden', flexShrink: 0 },
  msgAvatarImage: { width: 32, height: 32, borderRadius: 16 },
  msgAvatar: { width: 32, height: 32, borderRadius: 16, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  msgAvatarText: { fontSize: 11, fontWeight: 'bold' },
  msgContent: { maxWidth: '76%' },
  msgContentLeft: { alignItems: 'flex-start' },
  msgContentRight: { alignItems: 'flex-end' },
  choiceColumn: { alignSelf: 'flex-end', width: '100%' },
  senderName: { fontSize: 11, fontWeight: '600', marginBottom: 3, marginLeft: 4 },
  bubble: { borderRadius: 16, paddingHorizontal: 14, paddingVertical: 10 },
  bubbleMe: { borderBottomRightRadius: 4 },
  bubbleTutor: { borderBottomLeftRadius: 4 },
  bubbleText: { fontSize: 14, lineHeight: 20 },
  timestamp: { fontSize: 10, marginTop: 3, marginHorizontal: 4 },
  timestampRight: { alignSelf: 'flex-end' },
  inlineOptions: { marginBottom: 4, gap: 8, width: '100%' },
  optHint: { fontSize: 12, fontWeight: '500', marginBottom: 2, textAlign: 'right' },
  optBtn: { borderWidth: 1.5, borderRadius: 10, paddingHorizontal: 14, paddingVertical: 11 },
  optBtnFullWidth: { alignSelf: 'stretch' },
  optBtnLocked: { opacity: 0.92 },
  optBtnDisabled: { opacity: 0.55 },
  optBtnText: { fontSize: 13, fontWeight: '500', lineHeight: 18 },
  checkRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  checkIcon: { marginRight: 2 },
  checkbox: { width: 18, height: 18, borderRadius: 4, borderWidth: 2, justifyContent: 'center', alignItems: 'center' },
  validateBtn: { borderRadius: 10, paddingVertical: 12, alignItems: 'center', marginTop: 4 },
  validateBtnText: { fontSize: 14, fontWeight: '600' },
  freeRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 10 },
  freeInput: { flex: 1, borderWidth: 1, borderRadius: 12, paddingHorizontal: 14, paddingVertical: 10, maxHeight: 100, fontSize: 14 },
  sendBtn: { width: 44, height: 44, borderRadius: 22, justifyContent: 'center', alignItems: 'center' },
  completionBanner: { flexDirection: 'row', alignItems: 'center', gap: 8, borderWidth: 1, borderRadius: 12, padding: 14, marginTop: 8, marginBottom: 16 },
  completionText: { fontSize: 14, fontWeight: '600', flex: 1 },
  actionRow: { flexDirection: 'row', justifyContent: 'flex-end', paddingVertical: 12, paddingHorizontal: 4, gap: 8, marginTop: 8 },
  closePill: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, borderWidth: 1, backgroundColor: 'transparent' },
  closePillText: { fontSize: 14, fontWeight: '600' },
  nextPill: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20 },
  nextPillText: { fontSize: 14, fontWeight: '700' },
  emptyState: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 },
  emptyTitle: { fontSize: 18, fontWeight: '700', marginTop: 16, marginBottom: 8 },
  emptySubtitle: { fontSize: 14, textAlign: 'center' },
});

